import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Users, Minus, Plus, Loader2, X } from "lucide-react";
import { format, addDays, differenceInDays, parse } from "date-fns";
import { es, ptBR, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useLocale } from "@/contexts/LocaleContext";
import type { DateRange } from "react-day-picker";

interface SearchEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCheckIn: string;
  currentNumNights: string;
  currentAdults: string;
  currentChildrenAges?: string;
  onSearch: (params: {
    checkIn: string;
    numNights: number;
    adults: number;
    childrenAges: number[];
  }) => void;
  isSearching: boolean;
}

export function SearchEditModal({
  open,
  onOpenChange,
  currentCheckIn,
  currentNumNights,
  currentAdults,
  currentChildrenAges,
  onSearch,
  isSearching
}: SearchEditModalProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const getDateLocale = () => {
    switch (locale) {
      case 'pt-BR':
        return ptBR;
      case 'en':
        return enUS;
      default:
        return es;
    }
  };

  const dateLocale = getDateLocale();

  const initialCheckIn = parse(currentCheckIn, 'yyyy-MM-dd', new Date());
  const initialNumNights = parseInt(currentNumNights) || 7;

  const [dateRange, setDateRange] = useState<DateRange>({
    from: initialCheckIn,
    to: addDays(initialCheckIn, initialNumNights)
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [isSelectingNewDates, setIsSelectingNewDates] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [adults, setAdults] = useState(() => parseInt(currentAdults) || 2);
  const [childrenCount, setChildrenCount] = useState(() => 
    currentChildrenAges ? currentChildrenAges.split(',').filter(Boolean).length : 0
  );
  const [childrenAges, setChildrenAges] = useState<number[]>(() => 
    currentChildrenAges ? currentChildrenAges.split(',').filter(Boolean).map(Number) : []
  );

  // Use selected dates or fall back to initial dates
  const effectiveFrom = dateRange.from || initialCheckIn;
  const effectiveTo = dateRange.to || addDays(initialCheckIn, initialNumNights);
  const numNights = differenceInDays(effectiveTo, effectiveFrom);

  const handleSearch = () => {
    const checkInToUse = dateRange.from || initialCheckIn;
    const checkOutToUse = dateRange.to || addDays(initialCheckIn, initialNumNights);
    const nightsToUse = differenceInDays(checkOutToUse, checkInToUse);
    
    onSearch({
      checkIn: format(checkInToUse, 'yyyy-MM-dd'),
      numNights: nightsToUse,
      adults,
      childrenAges
    });
  };

  const handleChildrenCountChange = (newCount: number) => {
    if (newCount < 0 || newCount > 4) return;
    setChildrenCount(newCount);
    
    if (newCount < childrenAges.length) {
      setChildrenAges(childrenAges.slice(0, newCount));
    } else if (newCount > childrenAges.length) {
      setChildrenAges([...childrenAges, ...Array(newCount - childrenAges.length).fill(5)]);
    }
  };

  const handleChildAgeChange = (index: number, age: number) => {
    const newAges = [...childrenAges];
    newAges[index] = Math.max(0, Math.min(17, age));
    setChildrenAges(newAges);
  };

  const guestSummary = () => {
    const adultText = adults === 1 
      ? `1 ${t('common.adult')}` 
      : `${adults} ${t('common.adults')}`;
    
    if (childrenCount === 0) {
      return adultText;
    }
    
    const childText = childrenCount === 1 
      ? `1 ${t('common.child')}` 
      : `${childrenCount} ${t('common.children')}`;
    
    return `${adultText}, ${childText}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('searchEdit.title')}</DialogTitle>
          <DialogDescription>{t('searchEdit.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Dates */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              {t('search.dates')}
            </label>
            <Popover 
              open={datePickerOpen} 
              onOpenChange={(open) => {
                setDatePickerOpen(open);
                if (open) {
                  // Clear dates when opening the calendar so user can select fresh dates
                  setDateRange({ from: undefined, to: undefined });
                  setIsSelectingNewDates(true);
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full h-12 justify-start gap-3 bg-background border-border text-left font-normal",
                    !dateRange.from && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="h-5 w-5 text-primary shrink-0" />
                  {dateRange.from && dateRange.to ? (
                    <span className="truncate">
                      {format(dateRange.from, "dd MMM", { locale: dateLocale })} - {format(dateRange.to, "dd MMM yyyy", { locale: dateLocale })}
                    </span>
                  ) : isSelectingNewDates ? (
                    <span>{t('search.selectDates')}</span>
                  ) : (
                    <span className="truncate">
                      {format(initialCheckIn, "dd MMM", { locale: dateLocale })} - {format(addDays(initialCheckIn, initialNumNights), "dd MMM yyyy", { locale: dateLocale })}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-popover z-[9999]" align="start">
                <div className="flex flex-col">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) => {
                      if (range?.from && range?.to && 
                          range.from.getTime() === range.to.getTime()) {
                        return;
                      }
                      if (range) {
                        setDateRange(range as DateRange);
                      }
                      if (range?.from && range?.to) {
                        setDatePickerOpen(false);
                        setIsSelectingNewDates(false);
                      }
                    }}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      
                      if (date < today) {
                        return true;
                      }
                      
                      if (dateRange?.from) {
                        const checkInDate = new Date(dateRange.from);
                        checkInDate.setHours(0, 0, 0, 0);
                        const currentDate = new Date(date);
                        currentDate.setHours(0, 0, 0, 0);
                        
                        if (currentDate.getTime() === checkInDate.getTime()) {
                          return true;
                        }
                      }
                      
                      return false;
                    }}
                    initialFocus
                    numberOfMonths={1}
                    locale={dateLocale}
                    className="pointer-events-auto"
                  />
                  {dateRange?.from && (
                    <div className="border-t p-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          setDateRange({ from: undefined, to: undefined });
                          setIsSelectingNewDates(true);
                        }}
                      >
                        <X className="h-4 w-4 mr-2" />
                        {t('common.clearDates')}
                      </Button>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {numNights > 0 && (
              <p className="text-sm text-muted-foreground mt-1.5">
                {numNights} {numNights === 1 ? t('common.night') : t('common.nights')}
              </p>
            )}
          </div>

          {/* Guests */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              {t('search.guests')}
            </label>
            <Popover open={guestsOpen} onOpenChange={setGuestsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full h-12 justify-start gap-3 bg-background border-border text-left font-normal"
                >
                  <Users className="h-5 w-5 text-primary shrink-0" />
                  <span className="truncate">{guestSummary()}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4 bg-popover z-[9999]" align="start">
                <div className="space-y-4">
                  {/* Adults */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{t('search.adults')}</p>
                      <p className="text-sm text-muted-foreground">18+</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setAdults(Math.max(1, adults - 1))}
                        disabled={adults <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{adults}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setAdults(Math.min(6, adults + 1))}
                        disabled={adults >= 6}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Children */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{t('search.children')}</p>
                      <p className="text-sm text-muted-foreground">0-17</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleChildrenCountChange(childrenCount - 1)}
                        disabled={childrenCount <= 0}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{childrenCount}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleChildrenCountChange(childrenCount + 1)}
                        disabled={childrenCount >= 4}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Children Ages */}
                  {childrenCount > 0 && (
                    <div className="pt-4 border-t space-y-3">
                      <p className="text-sm font-medium">{t('search.childrenAge')}</p>
                      <div className="grid grid-cols-2 gap-3">
                        {childrenAges.map((age, index) => (
                          <div key={index} className="space-y-1">
                            <label className="text-xs text-muted-foreground">
                              {t('search.childLabel', { number: index + 1 })}
                            </label>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleChildAgeChange(index, age - 1)}
                                disabled={age <= 0}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-12 text-center text-sm">
                                {age} {age === 1 ? t('search.year') : t('search.years')}
                              </span>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleChildAgeChange(index, age + 1)}
                                disabled={age >= 17}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSearching}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSearch} disabled={isSearching || isSelectingNewDates}>
            {isSearching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('common.search')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
